import { ApiResultHandlingProvider } from "@jugl-web/domain-resources/api-results";
import {
  ApiErrorHandlingOutcome,
  ParsedApiError,
  apiErrorHasCode,
} from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { anonymousSessionState } from "@web-src/modules/auth/providers/AnonymousSessionProvider/anonymousSessionState";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { FC, PropsWithChildren, useCallback } from "react";

/**
 * Web configuration of the API result pipeline: routes product-wide errors
 * (plan limits paywall, write-restricted) to their alerts and preserves the
 * legacy global-handler policies (403 and anonymous-401 are swallowed).
 */
export const AppApiResultHandlingProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    subscriptionInfo,
    showPlanLimitsReachedAlert,
    showWriteRestrictedAlert,
  } = useEntityProvider();
  const { toast } = useToast();
  const { t } = useTranslations();

  const onSpecialError = useCallback(
    (error: ParsedApiError): ApiErrorHandlingOutcome => {
      if (error.type !== "http") {
        return "unhandled";
      }
      // NOTE: temporarily ignore 403 errors (parity with the legacy interceptor)
      if (error.status === 403) {
        return undefined;
      }
      if (error.status === 401 && anonymousSessionState.isAnonymous) {
        return undefined;
      }
      if (error.url?.includes("/web/verify-email")) {
        toast(
          t({
            id: "feedback.invalid-email-or-password",
            defaultMessage: "Invalid email or password",
          }),
          { variant: "error" }
        );
        return undefined;
      }
      if (subscriptionInfo && !subscriptionInfo.planIsActive) {
        return undefined;
      }
      if (apiErrorHasCode(error, "quota_limit_exceeded")) {
        // NOTE: order form submit is handled separately
        if (
          error.url?.includes("/order/form") &&
          error.url?.includes("/submit")
        ) {
          return undefined;
        }
        if (error.url?.includes("/share_link")) {
          showPlanLimitsReachedAlert("clientUpdates");
        } else if (error.url?.includes("/task")) {
          showPlanLimitsReachedAlert("tasks");
        } else if (error.url?.includes("/order/form")) {
          showPlanLimitsReachedAlert("orderForms");
        } else {
          showPlanLimitsReachedAlert("default");
        }
        return undefined;
      }
      if (apiErrorHasCode(error, "write_restricted")) {
        showWriteRestrictedAlert();
        return undefined;
      }
      // Alert / CX signup dialog is already shown via rateLimitEvents.
      if (error.status === 429) {
        return undefined;
      }
      return "unhandled";
    },
    [
      subscriptionInfo,
      showPlanLimitsReachedAlert,
      showWriteRestrictedAlert,
      toast,
      t,
    ]
  );

  return (
    <ApiResultHandlingProvider onSpecialError={onSpecialError}>
      {children}
    </ApiResultHandlingProvider>
  );
};
